/**
 * Pure colour helpers for the custom accent colour (meeting 2026-10-22,
 * item 27). Hex strings are normalised to lowercase `#rrggbb`; HSL uses
 * h in degrees [0, 360) and s / l as fractions [0, 1].
 */

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

type Rgb = [number, number, number];

/** `#abc`, `abc`, `#aabbcc` or `aabbcc` → `#aabbcc`; anything else → null. */
export function parseHex(input: string): string | null {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(input.trim());
  if (!m) return null;
  const digits = m[1].length === 3 ? m[1].replace(/./g, (c) => c + c) : m[1];
  return `#${digits.toLowerCase()}`;
}

function toRgb(hex: string): Rgb {
  const n = parseInt((parseHex(hex) ?? '#000000').slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function fromRgb([r, g, b]: Rgb): string {
  const part = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

export function hexToHsl(hex: string): Hsl {
  const [r, g, b] = toRgb(hex).map((v) => v / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: (h * 60 + 360) % 360, s, l };
}

export function hslToHex({ h, s, l }: Hsl): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return fromRgb([(r + m) * 255, (g + m) * 255, (b + m) * 255]);
}

/** Blend `pct` (0–1) of white into the colour. */
export function mixWithWhite(hex: string, pct: number): string {
  return fromRgb(toRgb(hex).map((v) => v + (255 - v) * pct) as Rgb);
}

/** Blend `pct` (0–1) of black into the colour. */
export function darken(hex: string, pct: number): string {
  return fromRgb(toRgb(hex).map((v) => v * (1 - pct)) as Rgb);
}

function luminance(hex: string): number {
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Buttons and the sidebar's active item put white text on the accent. */
export const MIN_WHITE_TEXT_CONTRAST = 3;

/** Lower the lightness until white text on the colour reaches 3:1. */
export function clampForWhiteText(hsl: Hsl): Hsl {
  let { l } = hsl;
  while (l > 0 && contrastRatio(hslToHex({ ...hsl, l }), '#ffffff') < MIN_WHITE_TEXT_CONTRAST) {
    l = Math.max(0, l - 0.01);
  }
  return { ...hsl, l };
}

/** The nine CSS variables a `theme-<name>` class sets (theme-styles.css). */
export function customSchemeVars(hex: string): Record<string, string> {
  const base = parseHex(hex) ?? '#000000';
  const hover = darken(base, 0.12);
  return {
    '--theme-primary': base,
    '--theme-text-link': base,
    '--theme-text-link-hover': hover,
    '--theme-button-primary': base,
    '--theme-button-primary-hover': hover,
    '--theme-border-focus': base,
    '--theme-input-focus-border': base,
    '--theme-table-row-hover-bg': mixWithWhite(base, 0.92),
    '--theme-table-row-selected-bg': mixWithWhite(base, 0.82),
  };
}
